/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Modal,
  ModalHeader,
  Cross,
  ModalContent,
  Space,
  Text,
  RadioGroup,
  Conditional,
  Button,
} from '@cashfree-intl/coherent';

import { CURRENCY_SYMBOL, INSTANT } from 'utils/constants';
import { numberWithCommas, roundOffNumber } from 'utils/common';

import { formatInstantSettlementsDailyLimit } from '../utils';
import { ACTIVE, ALL_DAYS, WORKING_DAYS_ONLY } from '../constants';
// import illustration from 'images/instant-settlements-illustration.svg';
import useClickOutside from '../../../utils/hooks';
import EVENTS from '../../../constants/analytics';
import Analytics from '../../../utils/analytics';

const INSTANT_SETTLEMENTS_CONFIG_TYPE = [
  {
    displayText: 'All days',
    label: 'Recommended',
    value: ALL_DAYS,
  },
  {
    displayText: 'Working Days Only',
    value: WORKING_DAYS_ONLY,
  },
  // {
  //   displayText: 'Holiday Only',
  //   value: 'HOLIDAY_ONLY',
  // },
];

const SAME_DAY_SETTLEMENT_VALUE = '99';

const INSTANT_SETTLEMENTS_CYCLE = [
  {
    displayText: '15 minutes',
    label: 'Recommended',
    value: '1',
  },
  {
    displayText: '1 Hour',
    value: '4',
  },
  {
    displayText: '3 Hours',
    value: '12',
  },
  {
    displayText: '6 Hours',
    value: '24',
  },
  {
    displayText: 'Same Day (daily at 9am & 5pm)',
    value: SAME_DAY_SETTLEMENT_VALUE,
    remarks: 'Same Day Settlements are available only on working days.',
  },
];

const ConfigureInstantSettlements = ({
  closeModal,
  confirmActivation,
  onDemandStatus,
  config,
  autoActivationEligible,
}) => {
  const [configType, setConfigType] = useState(
    INSTANT_SETTLEMENTS_CONFIG_TYPE[0].value,
  );
  const [cycle, setCycle] = useState(INSTANT_SETTLEMENTS_CYCLE[0].value);
  const [isLoading, setLoading] = useState(false);

  const modalRef = useRef(null);
  useClickOutside(modalRef, closeModal);

  const onConfigTypeChange = obj => () => {
    Analytics.track(EVENTS.SETTLEMENTS[INSTANT].CONFIGURE_INSTANT_SETTLEMENTS, {
      configType: obj.value,
    });
    setConfigType(obj.value);
  };

  const renderConfigType = () => {
    const radioOptions = INSTANT_SETTLEMENTS_CONFIG_TYPE.map(obj => ({
      label: () => (
        <label>
          <Space gap={1} alignItems="center">
            <Text
              variant="b14"
              color={
                cycle === SAME_DAY_SETTLEMENT_VALUE && obj.value === ALL_DAYS
                  ? 'placeholder'
                  : 'body'
              }
            >
              {obj.displayText}
            </Text>
            <Conditional if={obj.label}>
              <Text variant="b12" color="success">
                {obj.label}
              </Text>
            </Conditional>
          </Space>
        </label>
      ),
      name: 'configType',
      value: obj.value,
      checked: configType === obj.value,
      onChange: onConfigTypeChange(obj),
    }));

    return (
      <RadioGroup
        direction="column"
        fieldLabel=""
        radioOptions={radioOptions}
      />
    );
  };

  const onCycleChange = obj => () => {
    Analytics.track(EVENTS.SETTLEMENTS[INSTANT].CONFIGURE_INSTANT_SETTLEMENTS, {
      cycle: obj.value,
    });
    setCycle(obj.value);
    if (obj.value === SAME_DAY_SETTLEMENT_VALUE)
      setConfigType(WORKING_DAYS_ONLY);
  };

  const renderCycle = () => {
    const radioOptions = INSTANT_SETTLEMENTS_CYCLE.map(obj => ({
      label: () => (
        <label>
          <Space gap={1} alignItems="center">
            <Text variant="b14">{obj.displayText}</Text>
            <Conditional if={obj.label}>
              <Text variant="b12" color="success">
                {obj.label}
              </Text>
            </Conditional>
          </Space>
          <Conditional if={obj.remarks && cycle === obj.value}>
            <Text variant="b12" color="placeholder" className="radio-remark">
              {obj.remarks}
            </Text>
          </Conditional>
        </label>
      ),
      name: 'cycle',
      value: obj.value,
      checked: cycle === obj.value,
      onChange: onCycleChange(obj),
    }));

    return (
      <RadioGroup
        direction="column"
        fieldLabel=""
        radioOptions={radioOptions}
      />
    );
  };

  const activateInstantSettlements = () => {
    setLoading(true);
    confirmActivation({ configType, cycle }, setLoading);
  };

  const { limit, suffix } = formatInstantSettlementsDailyLimit(
    config.workingDayLimitInLakhs,
  );

  return (
    <Modal
      open
      $maxWidth={649}
      className="configure-instant-settlements-modal"
      ref={modalRef}
    >
      <ModalHeader>
        Configure Instant Settlements{' '}
        <Cross
          onClick={closeModal}
          data-event-name="Form_ConfigureInstantSettlements_Icon_Close"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <ModalContent
        className={isLoading ? 'disable-instant-settlements-modal' : ''}
      >
        {/* <div className="px-3 py-4"> */}
        <Space gap={cycle === SAME_DAY_SETTLEMENT_VALUE ? 5 : 20}>
          <div>
            <Text variant="b14" color="bodyLight">
              Receive Settlements Every:
            </Text>
            <div>{renderCycle()}</div>
          </div>
          <div>
            <Text variant="b14" color="bodyLight">
              Receive Settlements On:
            </Text>
            <div
              className={`${
                cycle === SAME_DAY_SETTLEMENT_VALUE ? 'disable-config-type' : ''
              }`}
            >
              {renderConfigType()}
            </div>
          </div>
        </Space>
        <Conditional if={onDemandStatus !== ACTIVE}>
          <>
            {autoActivationEligible ? (
              <Text variant="b12" color="bodyLight" className="mb-3 mt-2">
                Clicking Confirm Activation will activate Instant Settlements
                for your account and you can receive up to <br />{' '}
                {CURRENCY_SYMBOL.INR} {numberWithCommas(limit)} {suffix} per day
                charged at {roundOffNumber(config.workingDayRate)}% + GST. You
                will be notified when your daily limits are upgraded.
              </Text>
            ) : (
              <Text variant="b12" color="bodyLight" className="mb-2 mt-1">
                Once your request is approved, Instant Settlements will be
                activated at {roundOffNumber(config.workingDayRate)}% + GST.
              </Text>
            )}
          </>
        </Conditional>
        <Conditional if={onDemandStatus === ACTIVE}>
          <>
            {autoActivationEligible ? (
              <Text variant="b12" color="bodyLight" className="mb-2 mt-1">
                By clicking on “Confirm Activation” you will enable the instant
                settlements charged at {roundOffNumber(config.workingDayRate)}%
                + GST.
              </Text>
            ) : (
              <Text variant="b12" color="bodyLight" className="mb-2 mt-1">
                Once your request is approved, Instant Settlements will be
                activated at {roundOffNumber(config.workingDayRate)}% + GST.
              </Text>
            )}
          </>
        </Conditional>
        <div className="text-center">
          <Button
            primary
            disabled={isLoading}
            loading={isLoading}
            onClick={activateInstantSettlements}
            data-event-name="Primary_Button_ConfirmActivation"
          >
            {autoActivationEligible ? 'Confirm Activation' : 'Confirm Request'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

ConfigureInstantSettlements.propTypes = {
  closeModal: PropTypes.func.isRequired,
  confirmActivation: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  onDemandStatus: PropTypes.string.isRequired,
  autoActivationEligible: PropTypes.bool.isRequired,
};

export default ConfigureInstantSettlements;
